import { Component } from '@theme/component';

const ALLOWED_RESUME_EXTENSIONS = ['pdf', 'doc', 'docx'];

/**
 * Inline job application form for the job openings panel.
 *
 * Expands within a job card, validates a resume upload, and submits the
 * application as multipart form data to a third-party form endpoint
 * configured in the section settings.
 *
 * @typedef {Object} Refs
 * @property {HTMLButtonElement} toggle
 * @property {HTMLElement} panel
 * @property {HTMLFormElement} form
 * @property {HTMLInputElement} resume
 * @property {HTMLElement} resumeError
 * @property {HTMLButtonElement} submitButton
 * @property {HTMLInputElement} [honeypot]
 * @property {HTMLElement} success
 * @property {HTMLElement} error
 *
 * @extends {Component<Refs>}
 */
class JobApplicationForm extends Component {
  requiredRefs = ['toggle', 'panel', 'form', 'resume', 'submitButton', 'success', 'error'];

  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.refs.panel.inert = true;

    this.addEventListener(
      'keydown',
      (event) => {
        if (event.key === 'Escape' && this.expanded) {
          event.preventDefault();
          this.collapse();
        }
      },
      { signal: this.#abortController.signal }
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get expanded() {
    return this.hasAttribute('data-expanded');
  }

  toggle() {
    this.expanded ? this.collapse() : this.expand();
  }

  expand() {
    this.setAttribute('data-expanded', '');
    this.refs.toggle.setAttribute('aria-expanded', 'true');
    this.refs.panel.inert = false;

    const firstField = this.refs.form.querySelector('input:not([type="hidden"]):not([tabindex="-1"])');
    if (!(firstField instanceof HTMLElement)) return;

    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      firstField.focus();
    } else {
      this.refs.panel.addEventListener('transitionend', () => firstField.focus({ preventScroll: true }), {
        once: true,
        signal: this.#abortController.signal,
      });
    }
  }

  collapse() {
    this.removeAttribute('data-expanded');
    this.refs.toggle.setAttribute('aria-expanded', 'false');
    this.refs.panel.inert = true;
    this.refs.toggle.focus();
  }

  /**
   * Validates the selected resume file as soon as it is chosen.
   */
  validateResume() {
    this.#setResumeError('');

    const file = this.refs.resume.files?.[0];
    if (!file) return true;

    const extension = file.name.split('.').pop()?.toLowerCase() ?? '';
    if (!ALLOWED_RESUME_EXTENSIONS.includes(extension)) {
      this.#setResumeError(this.dataset.fileTypeError || 'Please upload a PDF, DOC, or DOCX file.');
      this.refs.resume.value = '';
      return false;
    }

    const maxFileSize = Number(this.dataset.maxFileSize) || 5242880;
    if (file.size > maxFileSize) {
      this.#setResumeError(this.dataset.fileSizeError || 'Resume must be under 5MB.');
      this.refs.resume.value = '';
      return false;
    }

    return true;
  }

  /**
   * @param {SubmitEvent} event
   */
  async handleSubmit(event) {
    event.preventDefault();

    const { form, honeypot, success } = this.refs;

    if (!form.reportValidity() || !this.validateResume()) return;

    if (honeypot?.value) {
      form.hidden = true;
      success.hidden = false;
      return;
    }

    this.#hideMessages();
    this.#setLoading(true);

    try {
      const response = await fetch(this.dataset.endpoint ?? '', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form),
      });

      if (response.ok) {
        form.hidden = true;
        success.hidden = false;
        success.focus();
      } else {
        this.#showError();
      }
    } catch (error) {
      this.#showError();
    } finally {
      this.#setLoading(false);
    }
  }

  /**
   * @param {string} message
   */
  #setResumeError(message) {
    this.refs.resumeError.textContent = message;
    this.refs.resumeError.hidden = message === '';
    if (message) {
      this.refs.resume.setAttribute('aria-invalid', 'true');
      this.refs.resume.focus();
    } else {
      this.refs.resume.removeAttribute('aria-invalid');
    }
  }

  /**
   * @param {boolean} isLoading
   */
  #setLoading(isLoading) {
    const { submitButton } = this.refs;
    submitButton.disabled = isLoading;

    const label = isLoading ? submitButton.dataset.loadingLabel : submitButton.dataset.defaultLabel;
    const labelElement = submitButton.querySelector('span');
    if (label && labelElement) labelElement.textContent = label;
  }

  #hideMessages() {
    this.refs.error.hidden = true;
    this.refs.success.hidden = true;
  }

  #showError() {
    this.refs.error.hidden = false;
  }
}

if (!customElements.get('job-application-form')) {
  customElements.define('job-application-form', JobApplicationForm);
}
